import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useMessages } from "@/hooks/useMessages";
import { useAuth } from "@/hooks/useAuth";
import { useRecordingPing } from "@/hooks/useRecordingPing";
import { phaseLabel } from "@/lib/walkaround";
import type { Message } from "@/types";

interface MessageThreadProps {
  bookingId: number;
  role: "renter" | "host";
  otherPartyName: string;
  disabled?: boolean;
}

const QUICK_REPLIES: Record<"renter" | "host", string[]> = {
  renter: ["I'm on my way to the pickup point.", "What's the exact meeting spot?", "I'll be 10 minutes late, sorry!"],
  host: ["I'm on my way, see you soon.", "I'll meet you at the main gate.", "The car is parked and ready."],
};

function formatTime(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" });
}

function formatDay(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  if (sameDay) return "Today";
  return date.toLocaleDateString("en-KE", { day: "numeric", month: "short" });
}

export default function MessageThread({ bookingId, role, otherPartyName, disabled = false }: MessageThreadProps) {
  const { user } = useAuth();
  const { messages, loading, error, sending, send } = useMessages(bookingId);
  const ping = useRecordingPing(bookingId);
  const [draft, setDraft] = useState("");
  const [sendError, setSendError] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const grouped = useMemo(() => {
    const groups: { day: string; items: Message[] }[] = [];
    messages.forEach((message) => {
      const day = formatDay(message.created_at);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.items.push(message);
      else groups.push({ day, items: [message] });
    });
    return groups;
  }, [messages]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!draft.trim() || sending) return;
    setSendError("");
    try {
      await send({ body: draft, senderRole: role });
      setDraft("");
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "Your message didn't send. Please try again.");
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl surface-card">
      <div className="flex items-center gap-3 border-b border-background-200/70 px-5 py-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-500/25 font-heading text-sm font-semibold text-secondary-200">
          {otherPartyName.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="truncate font-heading text-sm font-semibold text-foreground-950">{otherPartyName}</p>
          <p className="flex items-center gap-1.5 text-xs text-accent-300">
            <span className="flex h-1.5 w-1.5 rounded-full bg-accent-400"></span> Arrange your pickup here
          </p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-background-200/60 px-2.5 py-1 text-xs text-foreground-600">
          <i className="ri-lock-line"></i> Booking #{bookingId}
        </span>
      </div>

      {ping && (
        <div className="flex items-center gap-3 border-b border-primary-500/30 bg-primary-500/10 px-5 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500/20 text-secondary-400">
            <i className="ri-record-circle-line animate-rec-pulse text-lg"></i>
          </span>
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
              <span className="h-1.5 w-1.5 rounded-full bg-secondary-400 animate-rec-pulse"></span>
              {ping.recorderName} is filming a walkaround
            </p>
            <p className="mt-0.5 text-xs text-foreground-600">
              {phaseLabel(ping.phase)} clip in progress — open the app to watch it live.
            </p>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5" style={{ maxHeight: "420px" }}>
        {loading && (
          <div className="space-y-3">
            <div className="h-14 w-2/3 animate-pulse rounded-2xl bg-background-200/50" />
            <div className="ml-auto h-14 w-1/2 animate-pulse rounded-2xl bg-background-200/50" />
          </div>
        )}

        {!loading && error && (
          <p className="flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-3 text-sm text-secondary-300">
            <i className="ri-error-warning-line"></i> {error}
          </p>
        )}

        {!loading && !error && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/60 text-2xl text-foreground-500">
              <i className="ri-chat-3-line"></i>
            </span>
            <p className="mt-4 font-heading text-sm font-semibold text-foreground-950">Start the conversation</p>
            <p className="mt-1 max-w-xs text-xs text-foreground-600">
              Say hello and sort out where and when to meet for the pickup.
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          grouped.map((group) => (
            <div key={group.day} className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-background-200/70"></span>
                <span className="text-xs text-foreground-500">{group.day}</span>
                <span className="h-px flex-1 bg-background-200/70"></span>
              </div>
              {group.items.map((message) => {
                const mine = !!user && message.sender_id === user.id;
                return (
                  <div key={message.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                      <div
                        className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          mine
                            ? "bg-primary-500 text-background-50 rounded-br-md"
                            : "bg-background-200/70 text-foreground-900 rounded-bl-md"
                        }`}
                      >
                        {!mine && (
                          <p className="mb-1 text-xs font-medium text-foreground-600">
                            {message.sender_name || "Gari user"}
                            <span className="ml-1.5 capitalize text-foreground-500">· {message.sender_role || "member"}</span>
                          </p>
                        )}
                        {message.body}
                      </div>
                      <span className="mt-1 px-1 text-[11px] text-foreground-500">{formatTime(message.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-background-200/70 p-4">
        {!disabled && (
          <div className="mb-3 flex flex-wrap gap-2">
            {QUICK_REPLIES[role].map((quick) => (
              <button
                key={quick}
                type="button"
                onClick={() => setDraft(quick)}
                className="whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-500/50 hover:text-foreground-950"
              >
                {quick}
              </button>
            ))}
          </div>
        )}

        {sendError && (
          <p className="mb-3 flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-2.5 text-xs text-secondary-300">
            <i className="ri-error-warning-line"></i> {sendError}
          </p>
        )}

        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value.slice(0, 500))}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            rows={1}
            maxLength={500}
            disabled={disabled}
            placeholder={disabled ? "Messaging opens once you sign in" : "Type your message…"}
            className="max-h-32 min-h-[46px] flex-1 resize-none rounded-xl border border-background-200/70 bg-background-50 px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={disabled || sending || !draft.trim()}
            className="inline-flex h-[46px] items-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50"
          >
            {sending ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-fill"></i>}
            Send
          </button>
        </form>
      </div>
    </div>
  );
}